import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { CollectionModel } from '../../data-layer/collections/types';
import { CollectionStatusDrawer } from '../../ui/collection/CollectionStatusDrawer';
import { DataTestId } from '../../ui/layout/DataTestIdWrapper';
import { Typography } from '../../ui/typography/Typography';
import { CollectionStatus } from '../../ui/users/types';
import { contentActionsService } from '../../utils/ContentActionsService/ContentActionsService';
import { useEventSource } from '../../utils/LocationContext';
import { EventSource } from '../../utils/analyticsService';
import { ymTrackServiceRulesClick, ymTrackSupportClick } from '../../utils/metrika';
import { useBackButton } from '../../utils/useBackButton';
import { useTranslation } from '../../utils/useTranslation';
import { useSwitchDialog } from '../ModalRoute';
import { DEFAULT_CLOSE_ANIMATION_DELAY, DIALOGS } from '../constants';
import { useCurrentUser } from '../users/useCurrentUser';

const SUPPORT_LINK =
    'https://yandex.ru/support2/rythm/ru/buisness/moderation#trebovaniya-k-tekstam';

const statusMap = {
    [CollectionStatus.MODERATION]: 'Коллекция на модерации',
    [CollectionStatus.PUBLISHED]: 'Коллекция опубликована',
    [CollectionStatus.LIMITED]: 'Коллекция опубликована',
    [CollectionStatus.DECLINED]: 'Коллекция отклонена',
    [CollectionStatus.DRAFT]: 'Черновик',
};

const getDescriptionSlot = (
    status: CollectionStatus,
    onRulesClick: VoidFunction,
    modifiable: boolean,
) => {
    const t = useTranslation();
    if (status === CollectionStatus.MODERATION) {
        return (
            <Typography
                variant="body"
                dataTestId={DataTestId.COLLECTION_STATUS_DRAWER_DESCRIPTION_MODERATION}
            >
                {t('Поделиться коллекцией можно только после прохождения модерации')}
            </Typography>
        );
    }
    if (status === CollectionStatus.DECLINED) {
        return (
            <>
                <Typography variant="body">
                    {t('Коллекция отклонена по ')}
                    <Typography
                        variant="body"
                        display="inline"
                        as="a"
                        target="_blank"
                        href={SUPPORT_LINK}
                        color="link"
                        textDecoration="none"
                        onClick={onRulesClick}
                    >
                        {t('правилам сервиса')}
                    </Typography>
                    {modifiable ? t('. Поправьте название, и мы снова ее проверим.') : null}
                </Typography>
                {modifiable ? (
                    <Typography variant="body" display="inline" pt={2}>
                        {t(
                            'Если у вас остались вопросы, обращайтесь в поддержку, мы всегда на связи.',
                        )}
                    </Typography>
                ) : null}
            </>
        );
    }
};

interface CollectionStatusDialogState {
    collection: CollectionModel;
}

export const CollectionStatusDialog = () => {
    const { state, pathname } = useLocation();
    const { collection } = state as CollectionStatusDialogState;
    const onNavigateBack = useBackButton(pathname);
    const switchDialog = useSwitchDialog(true);
    const { eventSource } = useEventSource();
    const handleEditName = useCallback(() => {
        setTimeout(() => {
            switchDialog(DIALOGS.EDIT_COLLECTION, { collectionId: collection.id });
            contentActionsService.trackCollectionEditDialogOpen({
                id: collection.id,
                screen: eventSource ?? EventSource.COLLECTION,
            });
        }, DEFAULT_CLOSE_ANIMATION_DELAY);
    }, [switchDialog, collection.id, eventSource]);
    const closeDialog = useCallback(() => {
        setTimeout(onNavigateBack, DEFAULT_CLOSE_ANIMATION_DELAY);
    }, [onNavigateBack]);
    const currentUser = useCurrentUser();
    const onRulesClick = useCallback(() => {
        ymTrackServiceRulesClick({
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
            page: 'Коллекция',
            own: collection.modifiable,
            isPublic: collection.isPublic,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);
    const onSupportClick = useCallback(() => {
        ymTrackSupportClick({
            userLogin: currentUser?.login,
            businessId: currentUser?.profile?.publicId,
            page: 'Коллекция',
            own: collection.modifiable,
            isPublic: collection.isPublic,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, collection.modifiable]);

    return (
        <CollectionStatusDrawer
            handleClose={closeDialog}
            handleEditName={handleEditName}
            status={collection.status}
            modifiable={collection.modifiable}
            title={statusMap[collection.status]}
            descriptionSlot={getDescriptionSlot(
                collection.status,
                onRulesClick,
                collection.modifiable,
            )}
            currentUser={currentUser}
            onSupportClick={onSupportClick}
        />
    );
};
